/**
 * WONE-612: persist the last email a user successfully signed in with
 * so the unified entry screen can prefill it across short page reloads.
 *
 * Web policy (differs from mobile): logout clears the cache. The web
 * surface is more likely than mobile to be shared between users on the
 * same device, so we treat logout as "end of flow" and start the next
 * visitor fresh. Mid-flow back-nav between email-entry → chooser → OTP
 * is still handled by the in-tab ``auth_email_draft`` sessionStorage key
 * (cleared automatically on tab close).
 *
 * Branch.io ``?email=`` invite hints win over the cached value — that's
 * an explicit hint from the inviter, not a stale recollection from this
 * device.
 */

// WONE-612 review (Ed, PR #1172): surface localStorage failures via
// logError so we hear about them (QuotaExceeded, private-mode Safari,
// extension-blocked storage). The cache is non-load-bearing — the
// helpers still keep their "best effort, never throw" contract — but
// silent swallows hide a class of UX bugs we'd otherwise miss.
import { logError } from "./Function";

const STORAGE_KEY = "wone.auth.lastUsedEmail";

export function setLastUsedEmail(email: string): void {
  if (!email) return;
  try {
    localStorage.setItem(STORAGE_KEY, email.toLowerCase().trim());
  } catch (err) {
    logError("Failed to persist last-used email", err as Error);
  }
}

export function getLastUsedEmail(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (err) {
    logError("Failed to read last-used email", err as Error);
    return null;
  }
}

export function clearLastUsedEmail(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    logError("Failed to clear last-used email", err as Error);
  }
}
